import { useMemo, useState, useEffect } from "react";
import { FaFilter } from "react-icons/fa";
import { apiFetch } from "./api";
import UserModal from "./UserModal";
import CreateUserModal from "./CreateUserModal";
import { canonicalRole, uniqueFieldValues, getUserStatus } from "./utils";

const TWO_K_PR_PATTERN = /^[0-9]{2}:[0-5][0-9]:[0-5][0-9]$/;

// Cell definitions for inline editing: which roster fields are editable per column,
// and whether they apply to a given role (coaches only have team/squad on the roster sheet).
const EDITABLE_FIELDS = {
  team: { type: "select", optionsKey: "teams", roles: ["coach", "athlete"] },
  squad: { type: "select", optionsKey: "squads", roles: ["coach", "athlete"] },
  position: { type: "select", optionsKey: "positions", roles: ["athlete"] },
  graduation_year: { type: "number", roles: ["athlete"] },
  high_school: { type: "select", optionsKey: "highSchools", roles: ["athlete"] },
  two_k_pr: { type: "text", roles: ["athlete"], placeholder: "hh:mm:ss" },
};

export default function RosterList({
  roster = [],
  onUpdated,
  canEditRoster,
  coach,
  loading = false
}) {
  const [teamFilter, setTeamFilter] = useState(() => coach?.team || "all");
  const [squadFilter, setSquadFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("athlete");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [showFilters, setShowFilters] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(null); // selected user for popup
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { userId, field }
  const [editValue, setEditValue] = useState("");
  const [savingCell, setSavingCell] = useState(false);
  const [cellError, setCellError] = useState(null); // { userId, field, message }

  // Check if coach has users:manage permission
  const canManageUsers = coach?.permissions?.includes?.("users:manage") ?? coach?.can_manage_users ?? false;

  // Configured picklist values from database
  const [configuredTeams, setConfiguredTeams] = useState([]);
  const [configuredSquads, setConfiguredSquads] = useState([]);
  const [configuredPositions, setConfiguredPositions] = useState([]);
  const [highSchools, setHighSchools] = useState([]);

  const notifyUpdated = typeof onUpdated === "function" ? onUpdated : () => {};

  const startEditingCell = (record, field, e) => {
    e.stopPropagation();
    if (!canEditRoster || savingCell) return;
    const roleKey = canonicalRole(record);
    const fieldDef = EDITABLE_FIELDS[field];
    if (!fieldDef || !fieldDef.roles.includes(roleKey)) return;
    const userId = record.user_id ?? record.id;
    setCellError(null);
    setEditingCell({ userId, field });
    setEditValue(record[field] ?? "");
  };

  const cancelEditingCell = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const commitEditingCell = async (record) => {
    if (!editingCell) return;
    const { field } = editingCell;
    const userId = record.user_id ?? record.id;
    const originalValue = (record[field] ?? "").toString().trim();
    const nextValue = (editValue ?? "").toString().trim();

    if (nextValue === originalValue) {
      cancelEditingCell();
      return;
    }

    if (field === "two_k_pr" && nextValue && !TWO_K_PR_PATTERN.test(nextValue)) {
      setCellError({ userId, field, message: "Use hh:mm:ss, e.g. 00:07:15" });
      return;
    }

    setSavingCell(true);
    setCellError(null);
    try {
      await apiFetch(`/users/${userId}/roster`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: nextValue || null }),
      });
      setEditingCell(null);
      setEditValue("");
      notifyUpdated();
    } catch (err) {
      setCellError({ userId, field, message: err?.message || "Failed to save" });
    } finally {
      setSavingCell(false);
    }
  };

  // Load configured picklist values
  useEffect(() => {
    async function loadPicklists() {
      try {
        const [teamsData, squadsData, positionsData, highSchoolsData] = await Promise.all([
          apiFetch('/teams'),
          apiFetch('/squads'),
          apiFetch('/positions'),
          apiFetch('/high-schools')
        ]);
        setConfiguredTeams(teamsData.map(t => t.name).sort((a, b) => a.localeCompare(b)));
        setConfiguredSquads(squadsData.map(s => s.name).sort((a, b) => a.localeCompare(b)));
        setConfiguredPositions(positionsData.map(p => p.name).sort((a, b) => a.localeCompare(b)));
        setHighSchools(Array.isArray(highSchoolsData) ? highSchoolsData.map(h => h.name).sort((a, b) => a.localeCompare(b)) : []);
      } catch (e) {
        console.error('Failed to load picklists:', e);
        // Fall back to empty arrays if picklists can't be loaded
      }
    }
    loadPicklists();
  }, []);

  // Use configured picklists if available, otherwise fall back to unique values from roster
  const fallbackTeams = useMemo(() => uniqueFieldValues(roster, "team"), [roster]);
  const teams = configuredTeams.length > 0 ? configuredTeams : fallbackTeams;

  const fallbackSquads = useMemo(() => uniqueFieldValues(roster, "squad"), [roster]);
  const squads = configuredSquads.length > 0 ? configuredSquads : fallbackSquads;

  const fallbackPositions = useMemo(() => uniqueFieldValues(roster, "position"), [roster]);
  const positions = configuredPositions.length > 0 ? configuredPositions : fallbackPositions;

  const ageGroups = useMemo(() => uniqueFieldValues(roster, "age_group"), [roster]);

  const teamFilteredAthletes = useMemo(() => {
    const normalizedTeam = teamFilter.trim().toLowerCase();
    return roster.filter((r) => {
      const isActive = r.is_active !== false && r.is_active !== 0;
      if (!includeInactive && !isActive) return false;
      const roleKey = canonicalRole(r);
      if (roleKey !== "athlete") return false;
      if (teamFilter === "all") return true;
      return (r.team || "").trim().toLowerCase() === normalizedTeam;
    });
  }, [roster, teamFilter, includeInactive]);

  const rosterStats = useMemo(() => {
    const ageCounts = {};
    teamFilteredAthletes.forEach((athlete) => {
      const key = (athlete.age_group || "Unassigned").trim() || "Unassigned";
      ageCounts[key] = (ageCounts[key] || 0) + 1;
    });
    const ageGroups = Object.entries(ageCounts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, count]) => ({ label, count }));
    return {
      total: teamFilteredAthletes.length,
      ageGroups,
    };
  }, [teamFilteredAthletes]);

  // Filtered list
  const sortValueFor = (record, field) => {
    if (!record) return "";
    const raw =
      field === "name"
        ? record.name
        : record[field];
    return (raw ?? "").toString().trim().toLowerCase();
  };

  const list = useMemo(() => {
    const tq = q.trim().toLowerCase();
    return roster.filter(r => {
      const isActive = r.is_active !== false && r.is_active !== 0;
      if (!includeInactive && !isActive) return false;
      const roleKey = canonicalRole(r);
      const tOk = teamFilter === "all" || (r.team||"").trim().toLowerCase() === teamFilter.trim().toLowerCase();
      const agOk = ageGroupFilter === "all" || (r.age_group||"").trim().toLowerCase() === ageGroupFilter.trim().toLowerCase();
      const sOk = squadFilter === "all" || (r.squad||"").trim().toLowerCase() === squadFilter.trim().toLowerCase();
      const qOk = !tq || (r.name||"").toLowerCase().includes(tq);
      const rOk = roleFilter === "all" || roleKey === roleFilter;
      return tOk && sOk && agOk && qOk && rOk;
    }).sort((a,b)=> {
      const aValue = sortValueFor(a, sortBy);
      const bValue = sortValueFor(b, sortBy);
      // Keep blanks at the bottom regardless of direction
      if (!aValue && bValue) return 1;
      if (aValue && !bValue) return -1;
      const primary = aValue.localeCompare(bValue);
      if (primary !== 0) return primary;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [roster, teamFilter, squadFilter, roleFilter, ageGroupFilter, q, sortBy, includeInactive]);


  const activeFilterCount = [
    teamFilter !== "all",
    squadFilter !== "all",
    roleFilter !== "all",
    ageGroupFilter !== "all",
    includeInactive,
  ].filter(Boolean).length;

  const optionsByKey = { teams, squads, positions, highSchools };

  const renderEditableCell = (record, field, className, label) => {
    const userId = record.user_id ?? record.id;
    const roleKey = canonicalRole(record);
    const fieldDef = EDITABLE_FIELDS[field];
    const isEditable = canEditRoster && fieldDef && fieldDef.roles.includes(roleKey);
    const isEditing = editingCell && editingCell.userId === userId && editingCell.field === field;
    const error = cellError && cellError.userId === userId && cellError.field === field ? cellError.message : null;
    const displayValue = record[field] || "—";

    if (!isEditing) {
      return (
        <div
          className={`roster-col ${className} ${isEditable ? "roster-col-editable" : ""}`}
          data-label={label}
          onClick={isEditable ? (e) => startEditingCell(record, field, e) : undefined}
          title={isEditable ? "Click to edit" : undefined}
        >
          {displayValue}
          {error && <span className="roster-cell-error">{error}</span>}
        </div>
      );
    }

    const commonProps = {
      autoFocus: true,
      disabled: savingCell,
      value: editValue,
      onClick: (e) => e.stopPropagation(),
      onChange: (e) => setEditValue(e.target.value),
      onBlur: () => commitEditingCell(record),
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commitEditingCell(record);
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelEditingCell();
        }
      },
    };

    return (
      <div className={`roster-col ${className} roster-col-editing`} data-label={label} onClick={(e) => e.stopPropagation()}>
        {fieldDef.type === "select" ? (
          <select className="input roster-inline-input" {...commonProps}>
            <option value="">—</option>
            {(optionsByKey[fieldDef.optionsKey] || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            className="input roster-inline-input"
            type={fieldDef.type === "number" ? "number" : "text"}
            placeholder={fieldDef.placeholder}
            {...commonProps}
          />
        )}
        {error && <span className="roster-cell-error">{error}</span>}
      </div>
    );
  };

  return (
    <div className="stack roster-stack">
      <div className="card" style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Total Athletes</div>
            <div style={{ fontWeight: 700, fontSize: 22 }}>{rosterStats.total}</div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            {rosterStats.ageGroups.map((age) => (
              <div key={age.label} style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", padding: "8px 10px", borderRadius: 8, minWidth: 110 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {age.label}
                </div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text-strong)" }}>{age.count}</div>
              </div>
            ))}
            {rosterStats.ageGroups.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--muted)" }}>No age group data</div>
            )}
          </div>
        </div>
      </div>
      <div className="lineup-pill-bar">
        <div className="lineup-pill-filters">
          <input
            className="input roster-search"
            placeholder="Search by name"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
          />
          <button
            className={`btn-filter icon-only ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Filters"
            aria-label="Filters"
          >
            <FaFilter />
            {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
          </button>
          <label className="row" style={{ alignItems: "center", gap: 6, fontSize: 13, color: "var(--muted)" }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Include inactive
          </label>
          <select className="lineup-pill-select" value={sortBy} onChange={(e)=>setSortBy(e.target.value)}>
            <option value="name">Sort by name</option>
            <option value="team">Sort by team</option>
            <option value="squad">Sort by squad</option>
            <option value="age_group">Sort by age group</option>
            <option value="position">Sort by position</option>
          </select>
        </div>
        <div className="lineup-pill-actions">
          {canManageUsers && (
            <button
              className="btn"
              type="button"
              onClick={() => setShowCreateUser(true)}
            >
              New User
            </button>
          )}
        </div>
        {showFilters && (
          <div className="lineup-pill-filter-panel">
            <select className="lineup-pill-select" value={roleFilter} onChange={(e)=>setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              <option value="athlete">Athletes</option>
              <option value="coach">Coaches</option>
              <option value="parent">Parents</option>
            </select>
            <select className="lineup-pill-select" value={teamFilter} onChange={(e)=>setTeamFilter(e.target.value)}>
              <option value="all">All teams</option>
              {teams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="lineup-pill-select" value={squadFilter} onChange={(e)=>setSquadFilter(e.target.value)}>
              <option value="all">All squads</option>
              {squads.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="lineup-pill-select" value={ageGroupFilter} onChange={(e)=>setAgeGroupFilter(e.target.value)}>
              <option value="all">All age groups</option>
              {ageGroups.map(ag => <option key={ag} value={ag}>{ag}</option>)}
            </select>
            <button
              className="btn-clear-filters"
              onClick={() => {
                setRoleFilter("athlete");
                setTeamFilter(coach?.team || "all");
                setSquadFilter("all");
                setAgeGroupFilter("all");
              }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
  {loading && (
      <div className="card" style={{ padding: 8, marginTop: 8 }}>
        <span className="help">Loading roster…</span>
      </div>
    )}
      <div className="card roster-card">
        <div className="roster-header">
          <div className="roster-head roster-col-lg">Name</div>
          <div className="roster-head roster-col-md">Team</div>
          <div className="roster-head roster-col-md">Squad</div>
          <div className="roster-head roster-col-md">Position</div>
          <div className="roster-head roster-col-md roster-col-grad">Grad Year</div>
          <div className="roster-head roster-col-md roster-col-age">Age Group</div>
          <div className="roster-head roster-col-md roster-col-hs">High School</div>
          <div className="roster-head roster-col-md roster-col-pr">2K PR</div>
        </div>

        <div className="roster-list">
          {list.map(a => {
            const status = getUserStatus(a);
            const rowKey = a.user_id ?? a.id;
            return (
            <div
              key={rowKey}
              className={`roster-row ${!canEditRoster ? "view-only" : ""}`}
              onClick={() => setOpen(a)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setOpen(a);
                }
              }}
            >
              <div className="roster-col roster-col-lg">
                <div className="roster-name">
                  <span>{a.name || "—"}</span>
                  {canonicalRole(a) && (
                    <span className={`roster-role-chip roster-role-${canonicalRole(a)}`}>
                      {a.role || (canonicalRole(a) === "coach" ? "Coach" : canonicalRole(a) === "parent" ? "Parent" : "Athlete")}
                    </span>
                  )}
                  <span className={`roster-status-badge roster-status-${status.type}`}>
                    {status.label}
                  </span>
                </div>
              </div>
              {renderEditableCell(a, "team", "roster-col-md", "Team")}
              {renderEditableCell(a, "squad", "roster-col-md", "Squad")}
              {renderEditableCell(a, "position", "roster-col-md", "Position")}
              {renderEditableCell(a, "graduation_year", "roster-col-md roster-col-grad", "Grad Year")}
              <div className="roster-col roster-col-md roster-col-age" data-label="Age Group">{a.age_group || "—"}</div>
              {renderEditableCell(a, "high_school", "roster-col-md roster-col-hs", "High School")}
              {renderEditableCell(a, "two_k_pr", "roster-col-md roster-col-pr", "2K PR")}
            </div>
            );
          })}
          {list.length === 0 && (
            <div className="roster-empty">No roster members match your filters.</div>
          )}
        </div>
      </div>


      <UserModal
        user={open}
        onClose={() => setOpen(null)}
        onUpdated={notifyUpdated}
        onShowUser={(u) => setOpen(u)}
        canEditRoster={canEditRoster}
        canManageUsers={canManageUsers}
        roster={roster}
        teams={teams}
        squads={squads}
        positions={positions}
        highSchools={highSchools}
      />
      <CreateUserModal
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onCreated={notifyUpdated}
        teams={teams}
        squads={squads}
        positions={positions}
      />
    </div>
  );
}
